import React from 'react';
import { Check, Plus, Sparkles } from 'lucide-react';
import { Button } from '../common/Button';
import { GST_RATE } from '../../common/gst';

interface AddOnCardProps {
  name: string;
  price: number;
  originalPrice?: number;
  minutes: number;
  freeMinutes?: number;
  features: string[];
  disabled?: boolean;
  isPurchasing?: boolean;
  hasCouponApplied?: boolean;
  discountPercentage?: number;
  couponValidButNotApplicable?: boolean;
  couponMinPurchaseNotMet?: boolean;
  shouldShowPricing?: boolean;
  userLocation?: { country: string; countryCode: string } | null;
  onPurchase?: () => void;
}

export function AddOnCard({ 
  name, 
  price, 
  originalPrice,
  minutes, 
  freeMinutes = 0,
  features,
  disabled = false,
  isPurchasing = false,
  hasCouponApplied = false,
  discountPercentage = 0,
  couponValidButNotApplicable = false,
  couponMinPurchaseNotMet = false,
  shouldShowPricing = true,
  userLocation,
  onPurchase
}: AddOnCardProps) {
  const handlePurchase = () => {
    if (onPurchase) onPurchase();
    // Open the addon page with UTM parameters
    const platformUrl = import.meta.env.VITE_MOCKTRAIL_PLATFORM_URL;
    window.open(`${platformUrl}/subscriptions?utm_source=landing_page&utm_medium=addon_card&utm_campaign=buy_bundle&utm_content=${name.toLowerCase()}`, '_blank');
  };

  return (
    <div className={`
      relative rounded-2xl transition-transform duration-300 ease-out hover:scale-[1.02]
      ${disabled ? 'opacity-70' : ''}
      h-full
    `}>
      {/* Overlay for disabled state */}
      {disabled && (
        <div className="absolute inset-0 bg-background-light/10 dark:bg-background-dark/10 rounded-2xl z-10 backdrop-blur-[0.7px]"></div>
      )}
      
      {/* Coupon badge */}
      {hasCouponApplied && discountPercentage > 0 && (
        <div className="absolute -top-0 -right-0 z-10">
            <div className="bg-green-500 text-white px-3 py-1 rounded-bl-lg rounded-tr-xl text-sm font-semibold">
            {discountPercentage}% Off
          </div>
        </div>
      )}
      
      {/* Card with gradient border */}
      <div className="p-0 rounded-2xl bg-gradient-to-br from-primary-light/5 via-secondary-light/5 to-primary-light/5 dark:from-primary-dark/5 dark:via-secondary-dark/5 dark:to-primary-dark/5 h-full">
        {/* Glass container */}
        <div className="relative h-full p-5 md:p-6 rounded-xl bg-gradient-to-br from-background-light/80 to-surface-light/80 dark:from-background-dark/80 dark:to-surface-dark/80 backdrop-blur-md border border-border-light dark:border-border-dark flex flex-col">
          {/* Header */}
          <div className="flex items-start justify-between mb-2">
            <div>
              <h3 className="text-lg font-bold mb-2 text-text-primary-light dark:text-text-primary-dark">
                {name}
              </h3>
              <p className="text-text-secondary-light dark:text-text-secondary-dark text-sm">
                {minutes} {freeMinutes > 0 && <span className="text-green-600 dark:text-green-400">(includes {freeMinutes} free)</span>} minutes
              </p>
            </div>
            <div className={`text-right ${(freeMinutes !== undefined && freeMinutes > 0) || hasCouponApplied && discountPercentage > 0 ? 'mt-2' : ''}`}>
              <div className="text-right">
                {shouldShowPricing ? (
                  <>
                    {originalPrice && (
                      <div className="text-sm text-text-secondary-light dark:text-text-secondary-dark line-through mb-1">
                        ₹{originalPrice.toFixed(0)}
                      </div>
                    )}
                    <div className={`text-2xl font-bold ${hasCouponApplied ? 'text-green-600 dark:text-green-500' : 'text-primary-light dark:text-primary-dark'}`}>
                      ₹{price.toFixed(0)}
                    </div>
                    <p className="text-text-secondary-light dark:text-text-secondary-dark text-sm">
                      one-time
                    </p>
                  </>
                ) : (
                  <div className="text-center">
                    <div className="text-lg font-bold text-primary-light dark:text-primary-dark mb-1">
                      Request Trial
                    </div>
                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                      {userLocation?.country} pricing
                    </p>
                  </div>
                )}
              </div>
              {/* GST Text Display */}
              {price > 0 && (
                <div className="text-right">
                  <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark font-heading font-medium">
                    +{GST_RATE}% GST
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Features */}
          <ul className="space-y-3 mb-6 flex-grow">
            {features.map((feature, index) => (
              <li key={index} className="flex items-start gap-3">
                <span className={`mt-1 p-0.5 rounded-full ${
                  feature.includes('free minutes') 
                    ? 'bg-green-500/10 text-green-600 dark:text-green-400'
                    : 'bg-primary-light/10 dark:bg-primary-dark/10 text-primary-light dark:text-primary-dark'
                }`}>
                  <Check className="w-3 h-3" />
                </span>
                <span className={`text-sm ${
                  feature.includes('free minutes')
                    ? 'text-green-700 dark:text-green-300 font-medium'
                    : 'text-text-secondary-light dark:text-text-secondary-dark'
                }`}>
                  {feature}
                </span>
              </li>
            ))}
          </ul>

          {/* CTA Button */}
          <Button
            variant="secondary"
            onClick={onPurchase}
            disabled={disabled}
            className={`mt-auto ${shouldShowPricing ? 'w-full md:w-[35%]' : 'w-full md:w-[50%]'}`}
            leftIcon={shouldShowPricing ? <Plus className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
          >
              {shouldShowPricing ? 'Buy Add-On' : 'Signup & Request Trial'}
          </Button>
        </div>
      </div>
    </div>
  );
}